import { IconGoBack } from '@speedlo/ui/src/components/icons/specific/IconChevrons'
import { Grid } from '@speedlo/ui/src/components/layout/Box'
import { getGridPlacement } from '@speedlo/ui/src/utils/gridUtils'
import * as React from 'react'

import { useStickyStateCtx } from '~shared/components/Sticky/StickyStateProvider'
import { menuScreenElementIds } from '~shared/config/menuScreenElementIds'
import { AddonDetail } from '~shared/segments/AddonDetail'
import { AddonList } from '~shared/segments/AddonList'
import { FullScreenDrawer } from '~ui/components/content/Drawer/FullScreenDrawer'

import { useCartModalControlsCtx } from '../../Cart'
import { useProductDetailControlCtx, useProductDetailCtx } from '../contexts/ProductDetailProvider'
import { TProductDetailOverlay } from '../types'
import { ProductDetailAmountControl } from './ProductDetailAmountControl'
import { ProductDetailImage } from './ProductDetailImage'
import { ProductDetailInfo } from './ProductDetailInfo'
import { ProductNote } from './ProductNote'
import { SubmitCartItemButtonWithBack } from './SubmitCartItemButton'

type TProps = NoChildren & TProductDetailOverlay

const { addonCategoryScrollContainerId } = menuScreenElementIds

/**
 * Overlay drawer for product detail.
 */
export const ProductDetailOverlayDrawer: React.FC<TProps> = ({
  branchId,
  cannotOrderNote,
  stuckContentHeight,
  canCreateOrder = true,
}) => {
  const { isStuck: isHeadingStuck } = useStickyStateCtx()

  const { closeProductDetail } = useProductDetailControlCtx()
  const { categoryId } = useProductDetailCtx()
  const { openDrawer } = useCartModalControlsCtx()

  // re-opens cart drawer after closing product detail modal
  // this is a workaround for the case when product detail modal is opened from the cart drawer (editing cart item)
  // with chakra-ui drawer, drawers must either be nested or closed before opening another one, otherwise it is impossible to scroll the second drawer
  // https://github.com/chakra-ui/chakra-ui/issues/7254
  const closeMobileProductDetail = React.useCallback(() => {
    closeProductDetail()

    if (categoryId) return
    openDrawer()
  }, [categoryId, closeProductDetail, openDrawer])

  return (
    <FullScreenDrawer
      isOpen
      onClose={closeMobileProductDetail}
      headerProps={{
        shadow: `none`,
        overContent: true,
        closeIcon: IconGoBack,
        closeBtnBgColor: `tertiary`,
        closeBtnOffset: { top: `0rem`, left: isHeadingStuck ? `-6rem` : `0rem` },
      }}
      containerProps={{
        padding: { top: `none`, bottom: `medium`, left: `none`, right: `none` },
        id: addonCategoryScrollContainerId,
      }}
      footerProps={{
        content: (
          <Grid rowGap={`medium`} flexGrow={1}>
            {cannotOrderNote}
            <ProductDetailAmountControl canCreateOrder={canCreateOrder} />
            <SubmitCartItemButtonWithBack
              canCreateOrder={canCreateOrder}
              closeMobileProductDetail={closeMobileProductDetail}
            />
          </Grid>
        ),
      }}
    >
      <Grid
        bgColor={`bgColors.card`}
        minHeight={`4.5rem`}
        shadow={`bottomLong`}
        zIndex={1}
        position={`relative`}
      >
        <ProductDetailImage />
      </Grid>

      <ProductDetailInfo hasStickyHeading horizontalPadding={`medium`} />

      <Grid {...getGridPlacement({ col: `all` })} rowGap={`large`}>
        <AddonList stuckContentHeight={stuckContentHeight} />
        <ProductNote canCreateOrder={canCreateOrder} />
      </Grid>

      {/* nesting drawers to make the second one scrollable, https://github.com/chakra-ui/chakra-ui/issues/7254 */}
      <AddonDetail branchId={branchId} />
    </FullScreenDrawer>
  )
}
